'use client';

import { type ElementType, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledAnchor } from '../../hooks/use-disabled-anchor';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { useSlotContent } from '../../hooks/use-slot-content';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Icon } from '../icon/icon';
import { Slot } from '../slot';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { iconButtonRecipe } from './styles/icon-button.styles';
import type { IconButtonPropsInterface } from './types/icon-button.types';

/**
 * @since 8.42.0
 */

export const IconButton = forwardRef<HTMLButtonElement, IconButtonPropsInterface>(
  (props: IconButtonPropsInterface, forwardedRef) => {
    const context = useContext(ButtonGroupContext);

    const {
      asChild = false,
      emphasis = 'outlined',
      tone = 'neutral',
      children,
      className,
      css: cssProp,
      disabled = false,
      icon,
      onImpact = false,
      shape = 'square',
      size = 'md',
      tabIndex,
      title,
      type = 'button',
      ...rest
    } = props;

    const buttonContextProps = {
      tone: context.tone || tone,
      emphasis: context.emphasis || emphasis,
      size: context.size || size,
    };

    const styles = css(
      iconButtonRecipe.raw({ disabled, onImpact, shape, ...buttonContextProps }),
      cssProp,
    );

    const content = useSlotContent<'button'>({
      children,
      asChild,
      template: (childContent) => (
        <>
          <VisuallyHidden>{title}</VisuallyHidden>
          <Icon>{childContent}</Icon>
        </>
      ),
      fallback: icon,
      dependencies: [title, icon],
    });

    // If a component (ex: Carousel.NavControls) explicitly sets tabIndex to -1, don't let
    // useDisabledState override it.
    const a11yDisabledState = useDisabledState({
      disabled,
      focusable: tabIndex === -1 ? false : true,
    });

    const contentPlusA11yProps = useDisabledAnchor({
      disabled,
      asChild,
      children: content,
      a11yProps: a11yDisabledState,
    });

    const Component: ElementType = asChild ? Slot : 'button';

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="IconButton"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        tabIndex={tabIndex}
        type={asChild ? undefined : type}
        {...rest}
        {...a11yDisabledState}
      >
        {contentPlusA11yProps}
      </Component>
    );
  },
);

IconButton.displayName = 'IconButton';
